import { watch } from 'vue'

import {
  BaseballEventStatusId,
  BaseballIncidentId,
  CommonResultId,
} from '@collector/sportsapi-client-legacy'
import { Incident } from '@collector/sportsapi-types'
import { sportsApiClient } from '@desktop/globalState/sportsApiClient'
import {
  useIncidents,
  useParticipantResultsMaps,
  useParticipants,
} from '@desktop/views/Relation/composables'

import { eventStatusToResultIdMap } from '../configuration'
import { useEventDetailHalf } from './useEventDetailHalf'

const finishedRegularTimeEventStatuses: BaseballEventStatusId[] = [
  BaseballEventStatusId.FirstInning,
  BaseballEventStatusId.SecondInning,
  BaseballEventStatusId.ThirdInning,
  BaseballEventStatusId.FourthInning,
  BaseballEventStatusId.FifthInning,
  BaseballEventStatusId.SixthInning,
  BaseballEventStatusId.SeventhInning,
  BaseballEventStatusId.EighthInning,
  BaseballEventStatusId.NinthInning,
  BaseballEventStatusId.TenthInning,
  BaseballEventStatusId.EleventhInning,
  BaseballEventStatusId.TwelfthInning,
  BaseballEventStatusId.ThirteenthInning,
  BaseballEventStatusId.FourteenthInning,
  BaseballEventStatusId.FifteenthInning,
  BaseballEventStatusId.SixteenthInning,
  BaseballEventStatusId.SeventeenthInning,
  BaseballEventStatusId.EighteenthInning,
  BaseballEventStatusId.NineteenthInning,
  BaseballEventStatusId.TwentiethInning,
  BaseballEventStatusId.TwentyFirstInning,
  BaseballEventStatusId.TwentySecondInning,
  BaseballEventStatusId.TwentyThirdInning,
]

export function useOnFinishedRegularTime(): void {
  const { incidentsUpdate } = useIncidents()
  const { eventDetailHalfValue } = useEventDetailHalf()
  const { homeParticipant, awayParticipant } = useParticipants()
  const { homeParticipantResultsMap, awayParticipantResultsMap } =
    useParticipantResultsMaps()

  // methods
  function isFinishedRegularTimeIncident(incident: Incident): boolean {
    return (
      incident.incident_id === BaseballIncidentId.FinishedRegularTime &&
      incident.action !== 'delete' &&
      finishedRegularTimeEventStatuses.includes(
        incident.status_id as BaseballEventStatusId,
      )
    )
  }

  async function patchParticipantResult(
    incident: Incident,
    resultId: number,
  ): Promise<void> {
    const homeScore =
      Number(homeParticipantResultsMap.value[CommonResultId.Result]?.value) || 0
    const awayScore =
      Number(awayParticipantResultsMap.value[CommonResultId.Result]?.value) || 0
    const participant =
      awayScore > homeScore ? awayParticipant.value : homeParticipant.value
    if (!participant) return

    // An empty stats array must be sent to avoid API Internal Server Error 500
    await sportsApiClient.Events.Participants.patch(incident.event_id, [
      {
        id: participant.id,
        stats: [],
        results: [
          {
            id: resultId,
            value: '-1',
          },
        ],
      },
    ])
  }

  // init
  watch(incidentsUpdate, async (incidents) => {
    for (const incidentUpdate of incidents) {
      const { data: incident } = incidentUpdate
      const isTopHalf = eventDetailHalfValue.value === 'top'
      const shouldPatchParticipantResult =
        isTopHalf && isFinishedRegularTimeIncident(incident)

      if (shouldPatchParticipantResult) {
        const resultId = eventStatusToResultIdMap.get(
          incident.status_id as BaseballEventStatusId,
        )
        if (resultId) {
          await patchParticipantResult(incident, resultId)
        }
        break
      }
    }
  })
}
